import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../lib/supabase";
import { db } from "../lib/db";
import TranslationModal from "./TranslationModal";
import { todayISO, addDaysISO } from "../utils/chartHelpers";
const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const dAgo = (days) => { const d = new Date(); d.setDate(d.getDate() - days * 1); return d.toISOString(); };
const addDays = (n) => addDaysISO(n);
const TODAY = todayISO();

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), n = new Date();
  if (d.toDateString() === n.toDateString()) return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const diff = Math.floor((n - d) / 86400000);
  if (diff < 7) return d.toLocaleDateString("en-US", { weekday: "short" });
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
const fmtFull = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};
const daysFromToday = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(dateStr); d.setHours(0,0,0,0);
  return Math.round((d - today) / 86400000);
};
const getInitials = (name) => name ? name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() : "?";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
export const CURRENT_USER = "dr-walker";

export const STAFF = [
  { id: "dr-walker",  name: "Dr. R. Walker", role: "Orthopedic Surgeon",  color: "#0ea5a0", online: true  },
  { id: "dr-patel",   name: "Dr. Patel",     role: "Orthopedic Surgeon",  color: "#a855f7", online: true  },
  { id: "dr-chen-md", name: "Dr. Chen",      role: "Anesthesiologist",    color: "#3b82f6", online: false },
  { id: "dr-moss",    name: "Dr. Moss",      role: "Orthopedic Surgeon",  color: "#f59e0b", online: false },
  { id: "sarah-m",    name: "Sarah M.",      role: "Biller",              color: "#ec4899", online: true  },
  { id: "james-t",    name: "James T.",      role: "Biller",              color: "#10b981", online: true  },
  { id: "lisa-k",     name: "Lisa K.",       role: "Biller",              color: "#6366f1", online: false },
  { id: "front-desk", name: "Front Desk",    role: "Admin",               color: "#f97316", online: true  },
  { id: "ma-team",    name: "MA Team",       role: "Medical Assistant",   color: "#14b8a6", online: true  },
];
const getStaff = (id) => STAFF.find(s => s.id === id) || { id, name: id, role: "", color: "#8fa3b4", online: false };

// ─── MOCK DATA ─────────────────────────────────────────────────────────────────
export const MOCK_CHANNELS = [
  // Direct Messages
  { id: "CH-MA",       type: "direct", name: "MA Team",    members: ["dr-walker","ma-team"],                                                                                         last_message_at: dAgo(0.08) },
  { id: "CH-SARAH",    type: "direct", name: "Sarah M.",   members: ["dr-walker","sarah-m"],                                                                                         last_message_at: dAgo(0.30) },
  { id: "CH-DESK",     type: "direct", name: "Front Desk", members: ["dr-walker","front-desk"],                                                                                      last_message_at: dAgo(0.50) },
  { id: "CH-PATEL",    type: "direct", name: "Dr. Patel",  members: ["dr-walker","dr-patel"],                                                                                        last_message_at: dAgo(1.20) },
  { id: "CH-CHEN",     type: "direct", name: "Dr. Chen",   members: ["dr-walker","dr-chen-md"],                                                                                      last_message_at: dAgo(2.00) },
  { id: "CH-MOSS",     type: "direct", name: "Dr. Moss",   members: ["dr-walker","dr-moss"],                                                                                         last_message_at: dAgo(3.00) },
  { id: "CH-JAMES",    type: "direct", name: "James T.",   members: ["dr-walker","james-t"],                                                                                         last_message_at: dAgo(5.00) },
  { id: "CH-LISA",     type: "direct", name: "Lisa K.",    members: ["dr-walker","lisa-k"],                                                                                          last_message_at: dAgo(7.00) },
  // Group Channels
  { id: "CH-CLINICAL", type: "group",  name: "clinical-team",  members: ["dr-walker","dr-patel","dr-chen-md","dr-moss","ma-team"],                                                   last_message_at: dAgo(0.20), description: "Clinical staff coordination" },
  { id: "CH-BILLING",  type: "group",  name: "billing-team",   members: ["dr-walker","sarah-m","james-t","lisa-k"],                                                                  last_message_at: dAgo(0.40), description: "Billing team channel" },
  { id: "CH-STAFF",    type: "group",  name: "all-staff",      members: ["dr-walker","dr-patel","dr-chen-md","dr-moss","sarah-m","james-t","lisa-k","front-desk","ma-team"],          last_message_at: dAgo(2.00), description: "All staff announcements" },
  { id: "CH-URGENT",   type: "group",  name: "urgent-alerts",  members: ["dr-walker","dr-patel","dr-chen-md","dr-moss"],                                                             last_message_at: dAgo(4.00), description: "Clinical urgent alerts" },
];

export const MOCK_MESSAGES = [
  // CH-MA: Dr. Walker ↔ MA Team (re: Monroe, P004)
  { id: "M001", channel_id: "CH-MA",       sender: "ma-team",    body: "Dr. Walker, Mrs. Monroe in room 3 is ready. Vitals: BP 128/82, HR 74, SpO2 98%. She has last visit X-rays with her.",                                                is_urgent: false, is_read: true,  patient_id: "P004", created_at: dAgo(0.15), attachment_name: null },
  { id: "M002", channel_id: "CH-MA",       sender: "dr-walker",  body: "Thanks, I'll be right in. Is her prior auth for the cortisone injection confirmed?",                                                                                   is_urgent: false, is_read: true,  patient_id: "P004", created_at: dAgo(0.13), attachment_name: null },
  { id: "M003", channel_id: "CH-MA",       sender: "ma-team",    body: "Yes, auth confirmed with Medicare — Auth #MCR-2026-0091. Also, Mr. Hartley called to confirm his 10am appointment tomorrow.",                                          is_urgent: false, is_read: false, patient_id: null,   created_at: dAgo(0.10), attachment_name: null },
  // CH-SARAH: Dr. Walker ↔ Sarah M. — BCBS denial (re: Hartley, P001)
  { id: "M010", channel_id: "CH-SARAH",    sender: "sarah-m",    body: "Dr. Walker — BCBS denied the 29827 for Hartley with CO-4 (insufficient documentation). We need to appeal within 30 days or resubmit with the correct modifier.",       is_urgent: true,  is_read: true,  patient_id: "P001", created_at: dAgo(0.38), attachment_name: null },
  { id: "M011", channel_id: "CH-SARAH",    sender: "dr-walker",  body: "Let's appeal. I'll draft a letter of medical necessity today. Can you pull the operative report and original coding sheet for me to review?",                          is_urgent: false, is_read: true,  patient_id: "P001", created_at: dAgo(0.36), attachment_name: null },
  { id: "M012", channel_id: "CH-SARAH",    sender: "sarah-m",    body: "Already pulled and in the shared folder. You'll also need to submit a corrected claim with modifier 22 added. Timely filing deadline is this Thursday.",              is_urgent: false, is_read: false, patient_id: "P001", created_at: dAgo(0.33), attachment_name: null },
  // CH-DESK: Dr. Walker → Front Desk (re: Chen, P003)
  { id: "M020", channel_id: "CH-DESK",     sender: "dr-walker",  body: "Please schedule Mr. Chen for his 6-week post-op follow-up. He was discharged after trigger finger release last Tuesday. He prefers afternoon appointments.",            is_urgent: false, is_read: true,  patient_id: "P003", created_at: dAgo(0.55), attachment_name: null },
  { id: "M021", channel_id: "CH-DESK",     sender: "front-desk", body: "Done! Mr. Chen is scheduled for April 2nd at 2:00 PM in room 2. Confirmation sent to his email rchen@email.com. He confirmed he'll be there.",                        is_urgent: false, is_read: false, patient_id: "P003", created_at: dAgo(0.50), attachment_name: null },
  // CH-CLINICAL: OR block time discussion
  { id: "M030", channel_id: "CH-CLINICAL", sender: "dr-patel",   body: "Reminder: OR block time next Tuesday starts at 7:00 AM sharp. Cases: Hartley (right rotator cuff repair, ~2.5h) and Monroe (CMC arthroplasty right thumb, ~1.5h). Pre-op paperwork must be complete by Friday EOD.", is_urgent: false, is_read: true,  patient_id: null,   created_at: dAgo(0.28), attachment_name: null },
  { id: "M031", channel_id: "CH-CLINICAL", sender: "dr-walker",  body: "Thanks. For Monroe I'll need anesthesia pre-op clearance — BMI 34, HTN, on NSAIDs. She needs to hold NSAIDs 5 days prior. Also confirm she has a 12-lead EKG on file.",                                              is_urgent: false, is_read: true,  patient_id: "P004", created_at: dAgo(0.25), attachment_name: null },
  { id: "M032", channel_id: "CH-CLINICAL", sender: "dr-chen-md", body: "I can handle Monroe's pre-op eval — will schedule her for Monday morning. For the EKG, I don't see one in the last 30 days. Can someone get her in for one this week?", is_urgent: false, is_read: false, patient_id: "P004", created_at: dAgo(0.22), attachment_name: null },
  // CH-BILLING: timely filing deadline
  { id: "M040", channel_id: "CH-BILLING",  sender: "sarah-m",    body: "Team: timely filing deadline for ALL February claims is March 31st — that's 9 days away. We still have ~23 claims outstanding. Please prioritize this week.",           is_urgent: false, is_read: true,  patient_id: null,   created_at: dAgo(0.45), attachment_name: null },
  { id: "M041", channel_id: "CH-BILLING",  sender: "james-t",    body: "I have 8 claims left from February — all UHC. Will batch submit today. Does anyone have the updated modifier reference sheet for UHC 2026?",                          is_urgent: false, is_read: true,  patient_id: null,   created_at: dAgo(0.43), attachment_name: null },
  { id: "M042", channel_id: "CH-BILLING",  sender: "lisa-k",     body: "I'll email the UHC modifier guide — updated as of Jan 1. Also flagging: UHC is rejecting 59+XS combinations from last month. I'll send claim-specific details separately.", is_urgent: false, is_read: false, patient_id: null,   created_at: dAgo(0.41), attachment_name: null },
  // CH-PATEL: quick DM
  { id: "M050", channel_id: "CH-PATEL",    sender: "dr-patel",   body: "Walker — are you covering Monday morning if I'm in the OR? Just want to confirm coverage for outpatient.",                                                              is_urgent: false, is_read: true,  patient_id: null,   created_at: dAgo(1.20), attachment_name: null },
  { id: "M051", channel_id: "CH-PATEL",    sender: "dr-walker",  body: "Yes I've got it. I'll start at 8am.",                                                                                                                                  is_urgent: false, is_read: true,  patient_id: null,   created_at: dAgo(1.18), attachment_name: null },
];

export const MOCK_TASKS = [
  { id: "T001", title: "Appeal BCBS denial — Hartley 29827",       description: "CO-4 modifier issue. File corrected claim + appeal letter with letter of medical necessity.",                    assigned_to: "sarah-m",    assigned_by: "dr-walker", patient_id: "P001", due_date: addDays(1), priority: "urgent", status: "open",      created_at: dAgo(0.38), completed_at: null },
  { id: "T002", title: "Schedule Monroe 6-week follow-up",          description: "Schedule post-injection follow-up for thumb CMC. Patient prefers mornings.",                                     assigned_to: "front-desk", assigned_by: "dr-walker", patient_id: "P004", due_date: addDays(0), priority: "normal", status: "open",      created_at: dAgo(0.55), completed_at: null },
  { id: "T003", title: "Prior auth — Delgado MRI right wrist",      description: "Submit prior auth to Aetna for MRI right wrist. Dx: S52.501A (distal radius fx), G56.01 (CTS). Refer to visit 3/5.",  assigned_to: "ma-team",    assigned_by: "dr-walker", patient_id: "P002", due_date: addDays(3), priority: "normal", status: "open",      created_at: dAgo(0.22), completed_at: null },
  { id: "T004", title: "Upload & sign operative report — Chen",     description: "Upload trigger finger release op report and complete physician attestation.",                                     assigned_to: "dr-walker",  assigned_by: "dr-patel",  patient_id: "P003", due_date: addDays(2), priority: "normal", status: "completed", created_at: dAgo(1.00), completed_at: dAgo(0.10) },
];

const MESSAGE_TEMPLATES = [
  { id: "TPL1", label: "Prior auth needed",  body: "Please initiate prior authorization for [procedure] for patient [name]. Insurance: [carrier]. Diagnosis: [ICD-10]." },
  { id: "TPL2", label: "Ready for discharge", body: "Patient [name] is ready for discharge from room [room]. Please prepare discharge paperwork and arrange transport if needed." },
  { id: "TPL3", label: "Script called in",   body: "Rx for [drug] has been called in to [pharmacy] for [patient]. Please confirm receipt." },
  { id: "TPL4", label: "Imaging ordered",    body: "Imaging order placed for [patient]. [Study type] of [body part]. Priority: [routine/STAT]. Please schedule." },
  { id: "TPL5", label: "Follow up needed",   body: "Please schedule follow-up for [patient] in [timeframe] for [reason]." },
  { id: "TPL6", label: "Auth approved",      body: "Insurance authorization approved for [patient]. Auth #[number]. Procedure: [procedure]. Valid through [date]." },
  { id: "TPL7", label: "Chart review needed",body: "Please review chart for [patient] before [date]. Reason: [reason]." },
];

// ─── STAFF AVATAR ─────────────────────────────────────────────────────────────
function StaffAvatar({ staffId, size = 32, showDot = false }) {
  const s = getStaff(staffId);
  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <div style={{ width: size, height: size, borderRadius: "50%", background: s.color + "22", border: `1px solid ${s.color}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.38, fontWeight: 600, color: s.color }}>
        {getInitials(s.name)}
      </div>
      {showDot && <div style={{ position: "absolute", bottom: 0, right: 0, width: 9, height: 9, borderRadius: "50%", background: s.online ? "var(--green)" : "var(--text-3)", border: "2px solid var(--navy-2)" }} />}
    </div>
  );
}

// ─── TASK ITEM ────────────────────────────────────────────────────────────────
function TaskItem({ task, patients, onComplete, onDelete }) {
  const diff = daysFromToday(task.due_date);
  const dueCls = diff === null ? "" : diff < 0 ? "var(--red)" : diff === 0 ? "var(--amber)" : "var(--green)";
  const dueLabel = diff === null ? "" : diff < 0 ? `${Math.abs(diff)}d overdue` : diff === 0 ? "Due today" : `Due in ${diff}d`;
  const pt = patients?.find(p => p.id === task.patient_id);
  const assignee = getStaff(task.assigned_to);
  const assigner = getStaff(task.assigned_by);
  const done = task.status === "completed";

  return (
    <div style={{ padding: "10px 12px", borderRadius: 8, background: done ? "transparent" : "var(--navy-3)", border: `1px solid ${done ? "transparent" : "var(--border)"}`, marginBottom: 8, opacity: done ? 0.55 : 1 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <input type="checkbox" checked={done} onChange={() => onComplete && onComplete(task.id)} style={{ marginTop: 3, cursor: "pointer", accentColor: "var(--teal)" }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: done ? 400 : 500, color: done ? "var(--text-3)" : "var(--text-1)", textDecoration: done ? "line-through" : "none", marginBottom: 3 }}>{task.title}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {task.priority === "urgent" && !done && <span className="badge badge-red" style={{ fontSize: 10 }}>⚡ Urgent</span>}
            {pt && <span style={{ fontSize: 10, background: "var(--amber-lt)", color: "var(--amber)", padding: "1px 6px", borderRadius: 10, fontWeight: 500 }}>Re: {pt.first} {pt.last}</span>}
            {!done && dueLabel && <span style={{ fontSize: 10, color: dueCls, fontWeight: 500 }}>{dueLabel}</span>}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3 }}>→ {assignee.name} · by {assigner.name}</div>
        </div>
        {onDelete && <button onClick={() => onDelete(task.id)} style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "2px 4px" }}>✕</button>}
      </div>
    </div>
  );
}

// ─── TASKS PANEL ──────────────────────────────────────────────────────────────
function TasksPanel({ patients, patientFilter }) {
  const [tasks, setTasks] = useState(isDemoMode ? MOCK_TASKS : []);
  const [filter, setFilter] = useState("open");
  const [showForm, setShowForm] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const emptyForm = { title: "", description: "", assigned_to: "ma-team", patient_id: "", due_date: addDays(1), priority: "normal" };
  const [form, setForm] = useState(emptyForm);

  const filtered = tasks.filter(t => {
    if (patientFilter && t.patient_id !== patientFilter) return false;
    const diff = daysFromToday(t.due_date);
    if (filter === "open")      return t.status === "open";
    if (filter === "completed") return t.status === "completed";
    if (filter === "overdue")   return t.status === "open" && diff !== null && diff < 0;
    return true;
  });
  const openTasks = filtered.filter(t => t.status === "open");
  const doneTasks = tasks.filter(t => t.status === "completed" && (!patientFilter || t.patient_id === patientFilter));

  const handleComplete = async (id) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, status: "completed", completed_at: new Date().toISOString() } : t));
    try { await db.from("tasks").update({ status: "completed", completed_at: new Date().toISOString() }).eq("id", id); } catch {}
  };
  const handleDelete = (id) => setTasks(prev => prev.filter(t => t.id !== id));
  const handleSave = async () => {
    const task = { ...form, id: "T" + Date.now(), status: "open", assigned_by: "dr-walker", created_at: new Date().toISOString(), completed_at: null };
    setTasks(prev => [...prev, task]);
    setForm(emptyForm); setShowForm(false);
    try { await db.from("tasks").insert([task]); } catch {}
  };

  const openCount = tasks.filter(t => t.status === "open" && (!patientFilter || t.patient_id === patientFilter)).length;

  return (
    <div style={{ width: 260, flexShrink: 0, borderLeft: "1px solid var(--border)", display: "flex", flexDirection: "column", background: "var(--navy-2)", overflow: "hidden" }}>
      <div style={{ padding: "14px 14px 10px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>My Tasks {openCount > 0 && <span className="badge badge-amber" style={{ fontSize: 10, marginLeft: 4 }}>{openCount}</span>}</div>
          <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }} onClick={() => setShowForm(v => !v)}>+ New</button>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {[["all","All"],["open","Open"],["completed","Done"],["overdue","Late"]].map(([v,l]) => (
            <button key={v} onClick={() => setFilter(v)} className={`btn btn-sm ${filter===v?"btn-secondary":"btn-ghost"}`} style={{ fontSize: 10, padding: "3px 7px" }}>{l}</button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "10px 10px 0" }}>
        {/* New Task Form */}
        {showForm && (
          <div style={{ background: "var(--navy-4)", border: "1px solid var(--border)", borderRadius: 8, padding: "12px", marginBottom: 12 }}>
            <input className="input" placeholder="Task title..." value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={{ fontSize: 12, marginBottom: 6 }} />
            <textarea className="input" placeholder="Description (optional)" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} style={{ fontSize: 12, resize: "none", marginBottom: 6 }} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
              <select className="input" value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))} style={{ fontSize: 11 }}>
                {STAFF.filter(s => s.id !== CURRENT_USER).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <select className="input" value={form.patient_id} onChange={e => setForm(f => ({ ...f, patient_id: e.target.value }))} style={{ fontSize: 11 }}>
                <option value="">No patient</option>
                {(patients || []).map(p => <option key={p.id} value={p.id}>{p.first} {p.last}</option>)}
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 8 }}>
              <input type="date" className="input" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} style={{ fontSize: 11 }} />
              <select className="input" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} style={{ fontSize: 11 }}>
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" style={{ flex: 1, fontSize: 11 }} onClick={handleSave} disabled={!form.title}>Save Task</button>
            </div>
          </div>
        )}

        {/* Open tasks */}
        {openTasks.map(t => <TaskItem key={t.id} task={t} patients={patients} onComplete={handleComplete} onDelete={handleDelete} />)}
        {openTasks.length === 0 && !showForm && (
          <div style={{ textAlign: "center", padding: "24px 12px", color: "var(--text-3)", fontSize: 12 }}>
            {filter === "overdue" ? "No overdue tasks" : filter === "completed" ? "" : "No open tasks"}
          </div>
        )}

        {/* Completed tasks */}
        {filter !== "completed" && doneTasks.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <button onClick={() => setShowCompleted(v => !v)} style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer", fontSize: 11, padding: "4px 0", display: "flex", alignItems: "center", gap: 6 }}>
              {showCompleted ? "▾" : "▸"} Completed ({doneTasks.length})
            </button>
            {showCompleted && doneTasks.map(t => <TaskItem key={t.id} task={t} patients={patients} onComplete={handleComplete} onDelete={handleDelete} />)}
          </div>
        )}
        {filter === "completed" && doneTasks.map(t => <TaskItem key={t.id} task={t} patients={patients} onComplete={handleComplete} onDelete={handleDelete} />)}

        <div style={{ height: 16 }} />
      </div>
    </div>
  );
}

// ─── MESSAGE BUBBLE ───────────────────────────────────────────────────────────
function MessageBubble({ msg, isMine, patients }) {
  const s = getStaff(msg.sender);
  const pt = patients?.find(p => p.id === msg.patient_id);
  const [showTranslate, setShowTranslate] = useState(false);
  return (
    <>
      <div style={{ display: "flex", flexDirection: isMine ? "row-reverse" : "row", alignItems: "flex-end", gap: 8, marginBottom: 14 }}>
        {!isMine && <StaffAvatar staffId={msg.sender} size={28} />}
        <div style={{ maxWidth: "68%", display: "flex", flexDirection: "column", alignItems: isMine ? "flex-end" : "flex-start" }}>
          {!isMine && <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 3, marginLeft: 2 }}>{s.name}</div>}
          <div style={{
            background: isMine ? "var(--teal)" : "var(--navy-3)",
            color: isMine ? "#fff" : "var(--text-1)",
            padding: "9px 13px",
            borderRadius: isMine ? "12px 12px 3px 12px" : "12px 12px 12px 3px",
            fontSize: 13,
            lineHeight: 1.55,
            border: msg.is_urgent ? `2px solid var(--red)` : "none",
            position: "relative",
          }}>
            {msg.is_urgent && <div style={{ fontSize: 9, fontWeight: 700, color: "var(--red)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>⚡ URGENT</div>}
            {msg.body}
            {msg.attachment_name && (
              <div style={{ marginTop: 8, padding: "6px 10px", background: isMine ? "rgba(0,0,0,0.15)" : "var(--navy-4)", borderRadius: 6, display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                <span>📎</span>
                <div>
                  <div style={{ fontWeight: 500 }}>{msg.attachment_name}</div>
                  <div style={{ fontSize: 10, opacity: 0.7 }}>{msg.attachment_type || "File"}</div>
                </div>
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
            {pt && <span style={{ fontSize: 10, background: "var(--amber-lt)", color: "var(--amber)", padding: "1px 6px", borderRadius: 10, fontWeight: 500 }}>Re: {pt.first} {pt.last}</span>}
            <span style={{ fontSize: 10, color: "var(--text-3)" }}>{fmtTime(msg.created_at)}</span>
            {isMine && <span style={{ fontSize: 10, color: msg.is_read ? "var(--teal)" : "var(--text-3)" }}>{msg.is_read ? "✓✓" : "✓"}</span>}
            {!isMine && msg.body && (
              <button onClick={() => setShowTranslate(true)} title="Translate message" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", fontSize: 11, padding: "0 2px", lineHeight: 1 }}>🌐</button>
            )}
          </div>
        </div>
      </div>
      {showTranslate && (
        <TranslationModal
          text={msg.body}
          patient={pt}
          contentType="patient_message"
          onClose={() => setShowTranslate(false)}
        />
      )}
    </>
  );
}

// ─── TEMPLATE PICKER ─────────────────────────────────────────────────────────
function TemplatePicker({ onSelect, onClose }) {
  return (
    <div style={{ position: "absolute", bottom: "calc(100% + 8px)", left: 0, width: 340, background: "var(--navy-3)", border: "1px solid var(--border-2)", borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.4)", zIndex: 50 }}>
      <div style={{ padding: "10px 14px 6px", fontSize: 12, fontWeight: 600, color: "var(--text-3)", borderBottom: "1px solid var(--border)" }}>Message Templates</div>
      {MESSAGE_TEMPLATES.map(t => (
        <div key={t.id} onClick={() => { onSelect(t.body); onClose(); }} style={{ padding: "9px 14px", borderBottom: "1px solid var(--border)", cursor: "pointer", fontSize: 12 }} className="tr-clickable">
          <div style={{ fontWeight: 500, color: "var(--text-1)", marginBottom: 2 }}>{t.label}</div>
          <div style={{ color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.body}</div>
        </div>
      ))}
    </div>
  );
}

// ─── MESSAGE COMPOSER ─────────────────────────────────────────────────────────
function MessageComposer({ onSend, patients, linkedPatient: initialPatient }) {
  const [body, setBody] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [linkedPatient, setLinkedPatient] = useState(initialPatient || null);
  const [showPtSearch, setShowPtSearch] = useState(false);
  const fileRef = useRef(null);
  const textRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setAttachment({ name: file.name, data: reader.result, type: file.type, size: file.size });
    reader.readAsDataURL(file);
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };
  const handleSend = () => {
    if (!body.trim() && !attachment) return;
    onSend({ body: body.trim(), is_urgent: urgent, patient_id: linkedPatient?.id || null, attachment_name: attachment?.name || null, attachment_type: attachment?.type || null, attachment_data: attachment?.data || null });
    setBody(""); setUrgent(false); setAttachment(null); setLinkedPatient(initialPatient || null);
  };

  return (
    <div style={{ padding: "10px 16px 14px", borderTop: "1px solid var(--border)", background: "var(--navy-2)", flexShrink: 0, position: "relative" }}>
      {showTemplates && <TemplatePicker onSelect={(t) => { setBody(t); textRef.current?.focus(); }} onClose={() => setShowTemplates(false)} />}

      {/* Chips row */}
      <div style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
        {urgent && <span onClick={() => setUrgent(false)} style={{ fontSize: 10, background: "var(--red-lt)", color: "var(--red)", padding: "2px 8px", borderRadius: 10, cursor: "pointer", fontWeight: 600 }}>⚡ URGENT ✕</span>}
        {attachment && <span onClick={() => setAttachment(null)} style={{ fontSize: 10, background: "var(--navy-4)", color: "var(--text-2)", padding: "2px 8px", borderRadius: 10, cursor: "pointer" }}>📎 {attachment.name} ✕</span>}
        {linkedPatient && <span onClick={() => setLinkedPatient(null)} style={{ fontSize: 10, background: "var(--amber-lt)", color: "var(--amber)", padding: "2px 8px", borderRadius: 10, cursor: "pointer", fontWeight: 500 }}>Re: {linkedPatient.first} {linkedPatient.last} ✕</span>}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <textarea
          ref={textRef}
          value={body}
          onChange={e => setBody(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
          rows={2}
          style={{ flex: 1, background: "var(--navy-3)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", color: "var(--text-1)", fontSize: 13, fontFamily: "var(--font)", outline: "none", resize: "none", lineHeight: 1.5 }}
          onFocus={e => { e.target.style.borderColor = "var(--teal)"; }}
          onBlur={e => { e.target.style.borderColor = "var(--border)"; }}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", gap: 4 }}>
            <button title="Urgent" onClick={() => setUrgent(v => !v)} className="btn btn-ghost btn-sm" style={{ fontSize: 14, padding: "5px 8px", color: urgent ? "var(--red)" : "var(--text-3)" }}>⚡</button>
            <button title="Attach file" onClick={() => fileRef.current?.click()} className="btn btn-ghost btn-sm" style={{ fontSize: 14, padding: "5px 8px" }}>📎</button>
            <button title="Templates" onClick={() => setShowTemplates(v => !v)} className="btn btn-ghost btn-sm" style={{ fontSize: 14, padding: "5px 8px", color: showTemplates ? "var(--teal)" : "var(--text-3)" }}>📋</button>
            <div style={{ position: "relative" }}>
              <button title="Link patient" onClick={() => setShowPtSearch(v => !v)} className="btn btn-ghost btn-sm" style={{ fontSize: 14, padding: "5px 8px", color: linkedPatient ? "var(--amber)" : "var(--text-3)" }}>👤</button>
              {showPtSearch && (
                <div style={{ position: "absolute", bottom: "calc(100% + 6px)", right: 0, background: "var(--navy-3)", border: "1px solid var(--border-2)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.4)", zIndex: 50, width: 220, maxHeight: 200, overflowY: "auto" }}>
                  <div style={{ padding: "6px 10px", fontSize: 11, color: "var(--text-3)", borderBottom: "1px solid var(--border)" }}>Link to Patient</div>
                  {(patients || []).map(p => (
                    <div key={p.id} onClick={() => { setLinkedPatient(p); setShowPtSearch(false); }} style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12, borderBottom: "1px solid var(--border)" }} className="tr-clickable">
                      <div style={{ fontWeight: 500 }}>{p.first} {p.last}</div>
                      <div style={{ fontSize: 10, color: "var(--text-3)" }}>{p.mrn}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleSend} disabled={!body.trim() && !attachment} style={{ justifyContent: "center" }}>Send</button>
        </div>
      </div>
      <input ref={fileRef} type="file" style={{ display: "none" }} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={handleFile} />
    </div>
  );
}

// ─── MESSAGE THREAD ───────────────────────────────────────────────────────────
function MessageThread({ channel, messages, onSend, patients }) {
  const bottomRef = useRef(null);
  const [localMessages, setLocalMessages] = useState(messages);

  useEffect(() => { setLocalMessages(messages); }, [messages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [localMessages]);

  if (!channel) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-3)" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>💬</div>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Select a conversation</div>
        <div style={{ fontSize: 12 }}>Choose from the left panel or start a new message</div>
      </div>
    </div>
  );

  const other = channel.type === "direct" ? channel.members.find(m => m !== CURRENT_USER) : null;
  const otherStaff = other ? getStaff(other) : null;

  const handleSend = (msg) => {
    const newMsg = {
      id: "M" + Date.now(),
      channel_id: channel.id,
      sender: CURRENT_USER,
      is_read: false,
      created_at: new Date().toISOString(),
      ...msg,
    };
    setLocalMessages(prev => [...prev, newMsg]);
    onSend && onSend(newMsg);
    try { db.from("messages").insert([newMsg]); } catch {}
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
      {/* Channel header */}
      <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--border)", background: "var(--navy-2)", flexShrink: 0, display: "flex", alignItems: "center", gap: 12 }}>
        {channel.type === "direct" && other ? (
          <>
            <StaffAvatar staffId={other} size={36} showDot />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{otherStaff?.name}</div>
              <div style={{ fontSize: 11, color: otherStaff?.online ? "var(--green)" : "var(--text-3)" }}>
                {otherStaff?.role} · {otherStaff?.online ? "● Online" : "○ Away"}
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--teal-lt)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: "var(--teal)", fontWeight: 700 }}>#</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>#{channel.name}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>{channel.description || `${channel.members.length} members`}</div>
            </div>
          </>
        )}
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
        {localMessages.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-3)", fontSize: 13 }}>No messages yet. Start the conversation.</div>
        )}
        {localMessages.map(m => (
          <MessageBubble key={m.id} msg={m} isMine={m.sender === CURRENT_USER} patients={patients} />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <MessageComposer onSend={handleSend} patients={patients} linkedPatient={null} />
    </div>
  );
}

// ─── CHANNEL LIST ─────────────────────────────────────────────────────────────
function ChannelListPanel({ channels, messages, active, onSelect, onNewMessage }) {
  const unread = (chId) => messages.filter(m => m.channel_id === chId && m.sender !== CURRENT_USER && !m.is_read).length;
  const dms = channels.filter(c => c.type === "direct");
  const groups = channels.filter(c => c.type === "group");

  const renderItem = (ch) => {
    const cnt = unread(ch.id);
    const isDirect = ch.type === "direct";
    const otherId = isDirect ? ch.members.find(m => m !== CURRENT_USER) : null;
    const isActive = active?.id === ch.id;
    return (
      <div key={ch.id} onClick={() => onSelect(ch)}
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 6, cursor: "pointer", background: isActive ? "var(--teal-lt)" : "transparent", borderLeft: isActive ? "2px solid var(--teal)" : "2px solid transparent", marginBottom: 1 }}
        className="tr-clickable"
      >
        {isDirect && otherId ? (
          <StaffAvatar staffId={otherId} size={26} showDot />
        ) : (
          <div style={{ width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "var(--text-3)", flexShrink: 0 }}>#</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: cnt > 0 ? 600 : 400, color: cnt > 0 ? "var(--text-1)" : isActive ? "var(--teal)" : "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {isDirect ? ch.name : ch.name}
          </div>
        </div>
        {cnt > 0 && <span style={{ background: "var(--teal)", color: "#fff", fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10, flexShrink: 0 }}>{cnt}</span>}
      </div>
    );
  };

  return (
    <div style={{ width: 220, flexShrink: 0, borderRight: "1px solid var(--border)", background: "var(--navy-2)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Current user header */}
      <div style={{ padding: "14px 14px 10px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <StaffAvatar staffId={CURRENT_USER} size={30} showDot />
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-1)" }}>Dr. R. Walker</div>
            <div style={{ fontSize: 10, color: "var(--green)" }}>● Online</div>
          </div>
        </div>
        <button className="btn btn-primary btn-sm" style={{ width: "100%", justifyContent: "center", fontSize: 12 }} onClick={onNewMessage}>+ New Message</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "8px 6px" }}>
        {/* Direct Messages */}
        <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.07em", padding: "4px 8px 6px" }}>Direct Messages</div>
        {dms.map(renderItem)}

        {/* Channels */}
        <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.07em", padding: "14px 8px 6px" }}>Channels</div>
        {groups.map(renderItem)}
      </div>
    </div>
  );
}

// ─── NEW MESSAGE MODAL ────────────────────────────────────────────────────────
function NewMessageModal({ onSelect, channels, onClose }) {
  const [search, setSearch] = useState("");
  const staff = STAFF.filter(s => s.id !== CURRENT_USER && (!search || s.name.toLowerCase().includes(search.toLowerCase())));
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200 }}>
      <div style={{ background: "var(--navy-2)", border: "1px solid var(--border-2)", borderRadius: 12, width: 400, overflow: "hidden", boxShadow: "0 24px 64px rgba(0,0,0,0.5)" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>New Message</div>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: "12px 16px" }}>
          <input className="input" placeholder="Search people..." value={search} onChange={e => setSearch(e.target.value)} autoFocus />
        </div>
        <div style={{ maxHeight: 280, overflowY: "auto" }}>
          {staff.map(s => {
            const ch = channels.find(c => c.type === "direct" && c.members.includes(s.id) && c.members.includes(CURRENT_USER));
            return (
              <div key={s.id} onClick={() => { onSelect(ch || { id: "CH-NEW-" + s.id, type: "direct", name: s.name, members: [CURRENT_USER, s.id], last_message_at: null }); onClose(); }}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", cursor: "pointer", borderBottom: "1px solid var(--border)" }} className="tr-clickable"
              >
                <StaffAvatar staffId={s.id} size={32} showDot />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)" }}>{s.role}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── PATIENT MESSAGES PANEL ───────────────────────────────────────────────────
export function PatientMessagesPanel({ patient, onClose, patients }) {
  const [messages, setMessages] = useState((isDemoMode ? MOCK_MESSAGES : []).filter(m => m.patient_id === patient?.id).sort((a,b) => new Date(a.created_at) - new Date(b.created_at)));
  const [tasks, setTasks] = useState((isDemoMode ? MOCK_TASKS : []).filter(t => t.patient_id === patient?.id));
  const [showCompose, setShowCompose] = useState(false);
  const [recipient, setRecipient] = useState("ma-team");
  const [composeBody, setComposeBody] = useState("");
  const [urgent, setUrgent] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleSend = async () => {
    if (!composeBody.trim()) return;
    const msg = { id: "M"+Date.now(), channel_id: "CH-"+recipient, sender: CURRENT_USER, recipient, body: composeBody, is_urgent: urgent, is_read: false, patient_id: patient?.id, created_at: new Date().toISOString(), attachment_name: null };
    setMessages(prev => [...prev, msg]);
    setComposeBody(""); setUrgent(false); setShowCompose(false);
    try { await db.from("messages").insert([msg]); } catch {}
  };

  const handleCompleteTask = (id) => setTasks(prev => prev.map(t => t.id === id ? { ...t, status: "completed", completed_at: new Date().toISOString() } : t));

  return createPortal(
    <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 480, background: "var(--navy-2)", borderLeft: "1px solid var(--border)", zIndex: 960, display: "flex", flexDirection: "column", boxShadow: "-4px 0 28px rgba(0,0,0,0.45)" }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 20 }}>💬</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Messages</div>
          {patient && <div style={{ fontSize: 12, color: "var(--text-3)" }}>{patient.first} {patient.last} · {patient.mrn}</div>}
        </div>
        <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}>✕</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
        {/* Patient-linked messages */}
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>
          Linked Messages ({messages.length})
        </div>
        {messages.length === 0 && <div style={{ color: "var(--text-3)", fontSize: 13, marginBottom: 16 }}>No messages linked to this patient.</div>}
        {messages.map(m => (
          <div key={m.id} style={{ background: "var(--navy-3)", border: `1px solid ${m.is_urgent ? "var(--red)" : "var(--border)"}`, borderRadius: 8, padding: "10px 14px", marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
              <StaffAvatar staffId={m.sender} size={24} />
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{getStaff(m.sender).name}</span>
                <span style={{ fontSize: 11, color: "var(--text-3)", marginLeft: 8 }}>{fmtFull(m.created_at)}</span>
              </div>
              {m.is_urgent && <span className="badge badge-red" style={{ fontSize: 10 }}>⚡ Urgent</span>}
            </div>
            <div style={{ fontSize: 13, color: "var(--text-1)", lineHeight: 1.5 }}>{m.body}</div>
          </div>
        ))}
        <div ref={bottomRef} />

        {/* Tasks */}
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.07em", margin: "20px 0 12px" }}>
          Linked Tasks ({tasks.length})
        </div>
        {tasks.length === 0 && <div style={{ color: "var(--text-3)", fontSize: 13 }}>No tasks linked to this patient.</div>}
        {tasks.map(t => <TaskItem key={t.id} task={t} patients={patients} onComplete={handleCompleteTask} />)}
      </div>

      {/* Compose / New actions */}
      <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border)", flexShrink: 0 }}>
        {showCompose ? (
          <div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <select className="input" style={{ flex: 1, fontSize: 12 }} value={recipient} onChange={e => setRecipient(e.target.value)}>
                {STAFF.filter(s => s.id !== CURRENT_USER).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <button className="btn btn-ghost btn-sm" style={{ color: urgent ? "var(--red)" : "var(--text-3)" }} onClick={() => setUrgent(v => !v)}>⚡</button>
            </div>
            <textarea className="input" rows={3} value={composeBody} onChange={e => setComposeBody(e.target.value)} placeholder={`Message re: ${patient?.first} ${patient?.last}…`} style={{ fontSize: 13, resize: "none", marginBottom: 8, width: "100%" }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowCompose(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1, justifyContent: "center" }} onClick={handleSend} disabled={!composeBody.trim()}>Send Message</button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-primary" style={{ flex: 1, justifyContent: "center" }} onClick={() => setShowCompose(true)}>
              + New Message re: this patient
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── MESSAGES UNREAD WIDGET (Dashboard) ──────────────────────────────────────
export function MessagesUnreadWidget({ onNavigate }) {
  const unread = (isDemoMode ? MOCK_MESSAGES : []).filter(m => m.sender !== CURRENT_USER && !m.is_read);
  const urgentCount = unread.filter(m => m.is_urgent).length;

  if (unread.length === 0) return (
    <div className="stat-card stat-card-link" style={{ cursor: "pointer" }} onClick={() => onNavigate && onNavigate("messages")}>
      <div className="stat-label">Unread Messages</div>
      <div className="stat-value">0</div>
      <div className="stat-sub">All caught up</div>
    </div>
  );

  return (
    <div className="card" style={{ gridColumn: "1 / -1", marginBottom: 16 }}>
      <div className="card-header">
        <span className="card-title">💬 Unread Messages</span>
        {urgentCount > 0 ? <span className="badge badge-red">{urgentCount} urgent</span> : null}
        <span className="badge badge-teal">{unread.length} unread</span>
        <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }} onClick={() => onNavigate && onNavigate("messages")}>View All →</button>
      </div>
      {unread.slice(0, 3).map(m => (
        <div key={m.id} className="tr-clickable" onClick={() => onNavigate && onNavigate("messages")} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: "1px solid var(--border)", cursor: "pointer" }}>
          <StaffAvatar staffId={m.sender} size={28} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{getStaff(m.sender).name}</span>
              {m.is_urgent && <span className="badge badge-red" style={{ fontSize: 9 }}>⚡</span>}
              <span style={{ fontSize: 11, color: "var(--text-3)", marginLeft: "auto" }}>{fmtTime(m.created_at)}</span>
            </div>
            <div style={{ fontSize: 12, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.body}</div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => onNavigate && onNavigate("messages")}>Reply</button>
        </div>
      ))}
    </div>
  );
}

// ─── MESSAGES PAGE (Standalone) ───────────────────────────────────────────────
export default function MessagesPage({ patients }) {
  const [channels] = useState(isDemoMode ? MOCK_CHANNELS : []);
  const [allMessages, setAllMessages] = useState(isDemoMode ? MOCK_MESSAGES : []);
  const [activeChannel, setActiveChannel] = useState(isDemoMode ? MOCK_CHANNELS[0] : null);
  const [showNewMsg, setShowNewMsg] = useState(false);

  const channelMessages = allMessages
    .filter(m => m.channel_id === activeChannel?.id)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  const handleSend = (msg) => setAllMessages(prev => [...prev, msg]);

  const handleSelectChannel = (ch) => {
    setActiveChannel(ch);
    // Mark messages in this channel as read
    setAllMessages(prev => prev.map(m =>
      m.channel_id === ch.id && m.sender !== CURRENT_USER ? { ...m, is_read: true } : m
    ));
  };

  return (
    <div style={{ display: "flex", height: "calc(100vh - var(--header-h))", margin: "-24px", overflow: "hidden" }}>
      {showNewMsg && <NewMessageModal channels={channels} onSelect={handleSelectChannel} onClose={() => setShowNewMsg(false)} />}

      <ChannelListPanel
        channels={channels}
        messages={allMessages}
        active={activeChannel}
        onSelect={handleSelectChannel}
        onNewMessage={() => setShowNewMsg(true)}
      />

      <MessageThread
        channel={activeChannel}
        messages={channelMessages}
        onSend={handleSend}
        patients={patients}
      />

      <TasksPanel patients={patients} />
    </div>
  );
}
